import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Classifier } from '@octiga/microsoft-events/src/classifier';
import { Classification, MicrosoftEvent } from '@octiga/microsoft-events/src/interfaces/meta.interface';
import { combineLatest, filter, map, Observable, ReplaySubject, skipUntil } from 'rxjs';
import * as fromMsp from '../modules/msp/store';
import { loadASNs } from '../modules/msp/store/asn/actions';
import { loadEventRules } from '../modules/msp/store/event-rules/actions';
import { loadGeos } from '../modules/msp/store/geo/actions';
import { loadIps } from '../modules/msp/store/ip/actions';
import { client } from '../stores/client';
import { loadDomains } from '../stores/client/graph/domains/actions';
import { loadGraphUsers } from '../stores/client/graph/user/user.actions';
import { loadM365Ranges } from '../stores/root/octiga/m365-ranges/actions';
import { selectM365Ranges, selectM365RangesStatus } from '../stores/root.store';
import { environment } from 'projects/angular-clarity/src/environments/environment';
import { EventRule, EventRuleProcessed } from '../modules/msp/store/event-rules/model';

@Injectable()
export class ClassificationService {
    classifier: Classifier;

    ready$ = new ReplaySubject(1);

    constructor(private store: Store) {}

    private dispatcher(_tenant: string) {
        this.store.dispatch(loadGeos());
        this.store.dispatch(loadASNs());
        this.store.dispatch(loadIps());
        this.store.dispatch(loadDomains({ _tenant }));
        this.store.dispatch(loadGraphUsers({ _tenant }));
        this.store.dispatch(loadEventRules());
        this.store.dispatch(loadM365Ranges());
    }

    public classify(event: MicrosoftEvent): Observable<Classification> {
        return this.ready$.pipe(map(() => this.classifier.run(event)));
    }

    init(tenant_id: string) {
        const eventRulesStatus = this.store.select(fromMsp.selectEventRulesStatus).pipe(filter((s) => s.loaded));
        const geosStatus = this.store.select(fromMsp.selectMspGeosStatus).pipe(filter((s) => s.loaded));
        const userStatus = this.store.pipe(select(client(tenant_id).graph.users.status)).pipe(filter((s) => s.loaded));
        const domainsStatus = this.store
            .pipe(select(client(tenant_id).graph.domains.status))
            .pipe(filter((s) => s.loaded));
        const m365RangesStatus = this.store.select(selectM365RangesStatus).pipe(filter((s) => s.loaded));

        const ready$ = combineLatest([eventRulesStatus, geosStatus, userStatus, domainsStatus, m365RangesStatus]);

        this.dispatcher(tenant_id);

        return combineLatest([
            this.store.select(fromMsp.selectEventRulesAll),
            this.store.select(fromMsp.selectMspGeos),
            this.store.pipe(select(client(tenant_id).graph.users.all)),
            this.store.pipe(select(client(tenant_id).graph.domains.all)),
            this.store.select(selectM365Ranges),
        ])
            .pipe(skipUntil(ready$))
            .subscribe(([event_rules, geos, users, domains, m365Ranges]) => {
                const rules: EventRuleProcessed[] = [];
                for (const rule of event_rules as EventRule[]) {
                    const copy: EventRuleProcessed = {
                        ...rule,
                        geo: rule.geo
                            .map((id) => {
                                const geo = geos.find((geo) => geo.id === id);

                                return geo
                                    ? {
                                          center: {
                                              lat: geo.lat,
                                              lng: geo.lng,
                                          },
                                          radius: geo.radius,
                                      }
                                    : null;
                            })
                            .filter(Boolean),
                    };
                    rules.push(copy);
                }

                this.classifier = new Classifier(
                    tenant_id,
                    environment.appId,
                    users,
                    domains,
                    rules,
                    m365Ranges.map((range) => range.network),
                );

                this.ready$.next(true);
            });
    }
}
